"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.asl = exports.lsr = exports.lsl = exports.divs = exports.mult = exports.adds = exports.subb = exports.comp = exports.RegisterKinds = void 0;
exports.add = add;
exports.sub = sub;
exports.mul = mul;
exports.div = div;
exports.mod = mod;
exports.neg = neg;
exports.inc = inc;
exports.dec = dec;
exports.and = and;
exports.or = or;
exports.xor = xor;
exports.not = not;
exports.shl = shl;
exports.shr = shr;
exports.cmp = cmp;
exports.test = test;
exports.move = move;
exports.clear = clear;
exports.asr = asr;
exports.addc = addc;
exports.subc = subc;
const register_values_1 = require("./register-values");
exports.RegisterKinds = {
    B: "B",
    A: "A",
    I: "I",
    L: "L",
};
const REGISTER_BIT_WIDTHS = {
    B: 8,
    A: 8,
    I: 16,
    L: 32,
};
const MAX_UNSIGNED = {
    8: 0xff,
    16: 0xffff,
    32: 0xffffffff,
};
const SIGN_MASK = {
    8: 0x80,
    16: 0x8000,
    32: 0x80000000,
};
function getBitWidth(register) {
    return REGISTER_BIT_WIDTHS[register.kind];
}
function maskValue(value, bits) {
    const unsigned = value >>> 0;
    if (bits === 32) {
        return unsigned;
    }
    return unsigned & MAX_UNSIGNED[bits];
}
function resolveRightValue(registers, right) {
    if (typeof right === "number") {
        return right;
    }
    return (0, register_values_1.getIntValue)(registers, right);
}
function updateZeroSign(flags, value, bits) {
    const masked = maskValue(value, bits);
    flags.z = masked === 0;
    flags.s = (masked & SIGN_MASK[bits]) !== 0;
}
function isNegative(value, bits) {
    return (maskValue(value, bits) & SIGN_MASK[bits]) !== 0;
}
function setOverflowAdd(flags, left, right, result, bits) {
    const signLeft = isNegative(left, bits);
    const signRight = isNegative(right, bits);
    const signResult = isNegative(result, bits);
    flags.v = signLeft === signRight && signResult !== signLeft;
}
function setOverflowSub(flags, left, right, result, bits) {
    const signLeft = isNegative(left, bits);
    const signRight = isNegative(right, bits);
    const signResult = isNegative(result, bits);
    flags.v = signLeft !== signRight && signResult !== signLeft;
}
function getSignedValue(value, bits) {
    const masked = maskValue(value, bits);
    const signMask = SIGN_MASK[bits];
    if ((masked & signMask) === 0) {
        return masked;
    }
    return masked - (MAX_UNSIGNED[bits] + 1);
}
function getShiftAmount(value) {
    return value | 0;
}
function add(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const sum = value0 + value1;
    const result = maskValue(sum, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowAdd(flags, value0, value1, result, bits);
    flags.c = sum > MAX_UNSIGNED[bits];
}
function sub(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const diff = value0 - value1;
    const result = maskValue(diff, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowSub(flags, value0, value1, result, bits);
    flags.c = value0 < value1;
}
function mul(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    // Compute the full signed product. For 32-bit the JS Number domain is too narrow,
    // so use BigInt to preserve the upper half. Mirrors C# OpMult which casts to (UInt64)
    // the signed product and stores `result >> (len*8)` into arg1.
    const productBig = BigInt(getSignedValue(value0, bits)) * BigInt(getSignedValue(value1, bits));
    const totalBits = BigInt(bits * 2);
    const totalMask = (1n << totalBits) - 1n;
    const productUnsigned = ((productBig % (1n << totalBits)) + (1n << totalBits)) & totalMask;
    const lowMask = bits === 32 ? 0xffffffffn : (1n << BigInt(bits)) - 1n;
    const lowBig = productUnsigned & lowMask;
    const highBig = (productUnsigned >> BigInt(bits)) & lowMask;
    const result = Number(lowBig) >>> 0;
    const resultHigh = Number(highBig) >>> 0;
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    flags.v = false;
    if (typeof source !== "number") {
        (0, register_values_1.setIntValue)(registers, source, resultHigh);
    }
}
function div(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    let result = 0;
    let remainder = 0;
    let error = false;
    if (value1 === 0) {
        error = true;
    }
    else {
        try {
            switch (bits) {
                case 8:
                case 16: {
                    result = Math.trunc(value0 / value1);
                    remainder = value0 % value1;
                    break;
                }
                case 32: {
                    const numerator = value0 | 0;
                    const denominator = value1 | 0;
                    result = Math.trunc(numerator / denominator);
                    remainder = numerator % denominator;
                    break;
                }
            }
        }
        catch (errorThrown) {
            error = true;
        }
    }
    flags.v = false;
    updateZeroSign(flags, result, bits);
    if (error) {
        result = value0;
    }
    (0, register_values_1.setIntValue)(registers, destination, result);
    if (typeof source !== "number") {
        (0, register_values_1.setIntValue)(registers, source, remainder);
    }
}
function mod(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    let result = 0;
    let error = false;
    if (value1 === 0) {
        error = true;
    }
    else {
        try {
            switch (bits) {
                case 8:
                case 16:
                    result = value0 % value1;
                    break;
                case 32: {
                    const numerator = value0 | 0;
                    const denominator = value1 | 0;
                    result = numerator % denominator;
                    break;
                }
            }
        }
        catch (errorThrown) {
            error = true;
        }
    }
    flags.v = false;
    updateZeroSign(flags, result, bits);
    if (error) {
        result = value0;
    }
    (0, register_values_1.setIntValue)(registers, destination, result);
}
function neg(registers, flags, destination) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const diff = 0 - value0;
    const result = maskValue(diff, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowSub(flags, 0, value0, result, bits);
    flags.c = value0 !== 0;
}
function inc(registers, flags, destination) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const sum = value0 + 1;
    const result = maskValue(sum, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowAdd(flags, value0, 1, result, bits);
    flags.c = sum > MAX_UNSIGNED[bits];
}
function dec(registers, flags, destination) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const diff = value0 - 1;
    const result = maskValue(diff, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowSub(flags, value0, 1, result, bits);
    flags.c = value0 < 1;
}
function and(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const result = maskValue(value0 & value1, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function or(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const result = maskValue(value0 | value1, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function xor(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const result = maskValue(value0 ^ value1, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function not(registers, flags, destination) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const result = maskValue(~value0, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function shl(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const shift = getShiftAmount(resolveRightValue(registers, source));
    let result = value0;
    if (shift < 0) {
        // keep carry as-is
    }
    else if (shift === 0) {
        flags.c = false;
    }
    else {
        if (shift > bits) {
            flags.c = false;
        }
        else {
            const carryShift = bits - shift;
            const carryMask = Math.pow(2, carryShift);
            flags.c = (value0 & carryMask) !== 0;
        }
        if (shift >= bits) {
            result = 0;
        }
        else {
            result = maskValue((value0 << shift) >>> 0, bits);
        }
    }
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function shr(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const shift = getShiftAmount(resolveRightValue(registers, source));
    let result = value0;
    if (shift < 0) {
        // keep carry as-is
    }
    else if (shift === 0) {
        flags.c = false;
    }
    else {
        if (shift > bits) {
            flags.c = false;
        }
        else {
            const carryShift = shift - 1;
            const carryMask = Math.pow(2, carryShift);
            flags.c = (value0 & carryMask) !== 0;
        }
        if (shift >= bits) {
            result = 0;
        }
        else {
            result = maskValue(value0 >>> shift, bits);
        }
    }
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function cmp(registers, flags, left, right) {
    const bits = getBitWidth(left);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, left), bits);
    const value1 = maskValue(resolveRightValue(registers, right), bits);
    const diff = value0 - value1;
    const result = maskValue(diff, bits);
    updateZeroSign(flags, result, bits);
    setOverflowSub(flags, value0, value1, result, bits);
    flags.c = value0 < value1;
}
function test(registers, flags, left, right) {
    const bits = getBitWidth(left);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, left), bits);
    const value1 = maskValue(resolveRightValue(registers, right), bits);
    const result = maskValue(value0 & value1, bits);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
function move(registers, destination, source) {
    const value = resolveRightValue(registers, source);
    (0, register_values_1.setIntValue)(registers, destination, value);
}
function clear(registers, destination) {
    if (destination.kind === "S") {
        registers.setS(destination.index, "");
    }
    else if (destination.kind === "F") {
        registers.setF(destination.index, 0);
    }
    else {
        (0, register_values_1.setIntValue)(registers, destination, 0);
    }
}
exports.comp = cmp;
exports.subb = sub;
exports.adds = add;
exports.mult = mul;
exports.divs = div;
exports.lsl = shl;
exports.lsr = shr;
exports.asl = shl;
/**
 * ASR - Arithmetic shift right.
 *
 * Distinct from LSR: sign-extends from the high bit. Mirrors C# OpAsr semantics —
 * when shift > bits, fills value with the sign bit and sets carry to that sign bit;
 * when 0 < shift ≤ bits, carry is the bit at position (shift-1).
 */
function asr(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const shift = getShiftAmount(resolveRightValue(registers, source));
    let result = value0;
    const signMask = SIGN_MASK[bits];
    const signSet = (value0 & signMask) !== 0;
    if (shift < 0) {
        // don't touch carry
    }
    else if (shift === 0) {
        flags.c = false;
    }
    else {
        if (shift > bits) {
            flags.c = signSet;
        }
        else {
            const carryShift = shift - 1;
            const carryMask = Math.pow(2, carryShift);
            flags.c = (value0 & carryMask) !== 0;
        }
        if (shift >= bits) {
            // saturate: all-ones if sign was set, else zero
            result = signSet ? MAX_UNSIGNED[bits] : 0;
        }
        else {
            // arithmetic shift: sign-extend from the top
            const signed = getSignedValue(value0, bits);
            result = maskValue(signed >> shift, bits);
        }
    }
    (0, register_values_1.setIntValue)(registers, destination, result);
    flags.v = false;
    updateZeroSign(flags, result, bits);
}
/**
 * Add with carry (ADDC).
 * Adds source to destination plus the carry flag.
 */
function addc(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    const carryIn = flags.c ? 1 : 0;
    const sum = value0 + value1 + carryIn;
    const result = maskValue(sum, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowAdd(flags, value0, value1 + carryIn, result, bits);
    flags.c = sum > MAX_UNSIGNED[bits];
}
/**
 * Subtract with carry/borrow (SUBC).
 * Subtracts source from destination minus the carry flag (borrow).
 * C=1 means borrow input, C=1 output means borrow occurred.
 */
function subc(registers, flags, destination, source) {
    const bits = getBitWidth(destination);
    const value0 = maskValue((0, register_values_1.getIntValue)(registers, destination), bits);
    const value1 = maskValue(resolveRightValue(registers, source), bits);
    // Borrow is carry flag directly
    const borrow = flags.c ? 1 : 0;
    const diff = value0 - value1 - borrow;
    const result = maskValue(diff, bits);
    (0, register_values_1.setIntValue)(registers, destination, result);
    updateZeroSign(flags, result, bits);
    setOverflowSub(flags, value0, value1 + borrow, result, bits);
    flags.c = (value0 < value1 + borrow);
}
//# sourceMappingURL=arithmetic.js.map